import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "group/button relative inline-flex max-w-full min-w-0 items-center justify-center overflow-hidden rounded-[0.9rem] border border-transparent bg-clip-padding text-center text-sm font-semibold tracking-[-0.015em] whitespace-normal break-words transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/30 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:not-aria-[haspopup]:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/25 dark:aria-invalid:border-destructive/60 dark:aria-invalid:ring-destructive/45 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "border-primary/80 bg-[linear-gradient(135deg,var(--primary),color-mix(in_srgb,var(--primary)_82%,var(--accent)))] text-primary-foreground shadow-[0_8px_22px_color-mix(in_srgb,var(--primary)_18%,transparent)] hover:-translate-y-0.5 hover:brightness-[1.04] hover:text-primary-foreground hover:shadow-[0_12px_28px_color-mix(in_srgb,var(--primary)_24%,transparent)]",
        outline:
          "border-border/75 bg-card/72 text-foreground shadow-xs backdrop-blur-xl hover:-translate-y-0.5 hover:border-accent/45 hover:bg-accent/10 hover:text-accent hover:shadow-sm aria-expanded:border-accent/40 aria-expanded:bg-accent/10 aria-expanded:text-accent",
        secondary:
          "border-border/55 bg-secondary/78 text-secondary-foreground shadow-xs backdrop-blur-lg hover:-translate-y-0.5 hover:border-accent/25 hover:bg-secondary hover:text-secondary-foreground hover:shadow-sm aria-expanded:bg-secondary aria-expanded:text-secondary-foreground",
        ghost:
          "text-muted-foreground hover:bg-secondary/75 hover:text-foreground aria-expanded:bg-secondary aria-expanded:text-foreground",
        destructive:
          "border-destructive bg-destructive text-destructive-foreground shadow-sm hover:-translate-y-0.5 hover:bg-destructive/90 hover:text-destructive-foreground focus-visible:border-destructive focus-visible:ring-destructive/35",
        link: "text-accent underline-offset-4 hover:text-accent-emphasis hover:underline",
      },
      size: {
        default:
          "h-auto min-h-10 gap-2 px-4 py-2 has-data-[icon=inline-end]:pr-3.5 has-data-[icon=inline-start]:pl-3.5",
        xs: "h-auto min-h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 py-1 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-auto min-h-9 gap-1.5 rounded-lg px-3 py-1.5 text-[0.82rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: "h-auto min-h-12 gap-2 px-5 py-2.5 text-base has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        icon: "size-10 shrink-0",
        "icon-xs":
          "size-6 shrink-0 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        "icon-sm":
          "size-9 shrink-0 rounded-lg in-data-[slot=button-group]:rounded-lg",
        "icon-lg": "size-12 shrink-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
    }
>(({ className, variant = "default", size = "default", asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
})

Button.displayName = "Button"

export { Button, buttonVariants }